import { useCssVars as _useCssVars, unref as _unref, defineComponent as _defineComponent } from 'vue'
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import isToday from "dayjs/plugin/isToday";
import isBetween from "dayjs/plugin/isBetween";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { ref, computed, PropType } from "vue";
import {
  CalendarDisplayMode,
  CalendarDisplayModeType,
  CalendarEventInfo,
} from "@/types";
import ScrollTopControl from "./ScrollTopControl.vue";


const __default__ = {
  name: "EventCalendar",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
  eventList: {
    type: Array as PropType<CalendarEventInfo<any>[]>,
    default: () => [],
    required: true,
  },
  eventColorClassMap: {
    type: Map as PropType<Map<string, string>>,
  },
  initialSelectedDate: {
    type: String,
    default: dayjs().format("YYYY-MM-DD"),
  },
  displayMode: {
    type: String as PropType<CalendarDisplayModeType>,
    default: CalendarDisplayMode.Chip,
  },
},
  emits: ["onSelectDate", "onChangeMonth", "onClickClose"],
  setup(__props, { expose: __expose, emit: __emit }) {
  __expose();

_useCssVars(_ctx => ({
  "43ed34be-gridColumns": (gridColumns.value),
  "43ed34be-gridStartIndex": (gridStartIndex.value)
}))

dayjs.locale("ko");
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isToday);
dayjs.extend(isBetween);
dayjs.extend(customParseFormat);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.tz.setDefault("Asia/Seoul");

const props = __props;

const emit = __emit;

const calendarContents = ref<HTMLElement>();

// 초기값 설정
const initialDate = dayjs(props.initialSelectedDate);
const year = ref(initialDate.year());
const month = ref(initialDate.month()); // 0 ~ 11
const selectedDate = ref(props.initialSelectedDate);

const getEventColorClass = (eventTitle: string) => {
  const event = eventTitle.replace(/\d+/g, "").trim();
  return props.eventColorClassMap?.get(event) ?? "";
};

const hasEventOnDayOfWeek = (dayOfWeekIndex: number) => {
  return props.eventList.some(
    (info) => dayjs(info.date).day() === dayOfWeekIndex
  );
};
const shouldShowSaturday = computed(() => hasEventOnDayOfWeek(6));
const shouldShowSunday = computed(() => hasEventOnDayOfWeek(0));

const getCountEvent = (eventTitle: string) => {
  const eventCount = eventTitle.replace(/\D/g, "");
  return eventCount.length ? +eventCount : 1;
};

// 달력(월) 계산/렌더링
const formattedMonth = computed(() =>
  (month.value + 1).toString().padStart(2, "0")
);

// 달력(일) 계산/렌더링
const dates = computed(() => {
  const dateList = [];
  const firstDate = dayjs(new Date(year.value, month.value, 1));
  const daysInMonth = firstDate.daysInMonth();

  for (let i = 1; i <= daysInMonth; i++) {
    const dayJs = dayjs(new Date(year.value, month.value, i));
    const dayOfWeek = dayJs.day();

    if (!shouldShowSunday.value && dayOfWeek === 0) continue;
    if (!shouldShowSaturday.value && dayOfWeek === 6) continue;

    dateList.push(dayJs);
  }

  return dateList;
});

const gridStartIndex = computed(() =>
  shouldShowSunday.value ? dates.value[0]?.day() + 1 : dates.value[0]?.day()
);
const gridColumns = computed(() => {
  let column = 5;
  if (shouldShowSunday.value) column += 1;
  if (shouldShowSaturday.value) column += 1;

  return column;
});

const onClickDay = (date: dayjs.Dayjs) => {
  const targetEvent = props.eventList.find(
    (event) => event.date === dayjs(date).format("YYYY-MM-DD")
  );
  if (!targetEvent) return;

  emit("onSelectDate", targetEvent);
};

const changeMonth = (setNext: boolean) => {
  let newMonth = setNext ? month.value + 1 : month.value - 1;

  if (newMonth < 0) {
    month.value = 11;
    year.value -= 1;
  } else if (newMonth > 11) {
    month.value = 0;
    year.value += 1;
  } else {
    month.value = newMonth;
  }

  emitOnChangeMonth();
};

const emitOnChangeMonth = () => {
  const monthText = dayjs()
    .year(year.value)
    .month(month.value)
    .format("YYYY-MM");
  emit("onChangeMonth", monthText);
};

const __returned__ = { props, emit, calendarContents, initialDate, year, month, selectedDate, getEventColorClass, hasEventOnDayOfWeek, shouldShowSaturday, shouldShowSunday, getCountEvent, formattedMonth, dates, gridStartIndex, gridColumns, onClickDay, changeMonth, emitOnChangeMonth, get dayjs() { return dayjs }, get CalendarDisplayMode() { return CalendarDisplayMode }, ScrollTopControl }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})